import React, { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../../../i18n/localeUtils'
import { v4 as uuidv4 } from 'uuid'
import {
    Button,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import { RegisteredCheckBox } from '../../../../components/fields/CheckBox'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatInteger,
    formatMoney,
} from '../../../../packages/utils/numberFormatters'
import { isBefore } from 'date-fns'
import { getConvertibleLoanAmount } from '../../../../components/utils/convertibleUtils'
import { ActionFormWithId } from './ActionForm'
import { ActionDialog } from './AdminEventActionDialog'

function parseOptionalLoanAmount(value: string | number | null): number | null {
    if (value === null || value === '') return null
    const n = typeof value === 'number' ? value : parseFloat(value)
    return Number.isFinite(n) ? n : null
}

const setTempId = (convertibleAction: ActionFormWithId) => {
    return {
        ...convertibleAction,
        tempId: convertibleAction.tempId || convertibleAction.id || uuidv4(),
    }
}

function computeOutstandingLoanBalance(actions: ActionFormWithId[]): number {
    const sorted = [...actions].sort((a, b) => (isBefore(new Date(a.recordDate), new Date(b.recordDate)) ? -1 : 1))

    const totalLoanAmount = getConvertibleLoanAmount({ convertibleActions: sorted } as ConvertibleEntity) ?? 0

    const balance = sorted.reduce((acc, curr) => {
        if (curr.repayment !== null) {
            return acc - parseFloat(curr.repayment)
        }
        if (curr.convertedShares !== null && curr.conversionPrice !== null) {
            return acc - curr.convertedShares * parseFloat(curr.conversionPrice)
        }
        return acc
    }, totalLoanAmount)

    return balance ?? 0
}

function EventsFields({
    convertible,
    setIsCustomFormDirty,
}: {
    convertible: ConvertibleEntity
    setIsCustomFormDirty: (v: boolean) => void
}) {
    const [actionFormOpen, setActionFormOpen] = useState(false)
    const [actionToEdit, setActionToEdit] = useState<ActionFormWithId | null>(null)
    const formMethods = useFormContext()
    const formValues = formMethods.getValues()
    const [convertibleActions, setConvertibleActions] = useState<ActionFormWithId[]>(
        ((formValues.convertibleActions as ActionFormWithId[]) || []).map(setTempId),
    )
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()

    const handleCloseEventDialog = () => {
        setActionFormOpen(false)
    }

    const editFormOpen = (convertibleAction: ActionFormWithId) => {
        const { createdAt, updatedAt, ...rest } = convertibleAction
        setActionToEdit(rest)
        setActionFormOpen(true)
    }
    const newFormOpen = () => {
        setActionToEdit(null)
        setActionFormOpen(true)
    }

    const syncLoanAmountFromLoanActions = (actions: ActionFormWithId[]) => {
        const withLoan = actions.filter(
            (a) =>
                a.actionType === 'new_loan' && parseOptionalLoanAmount(a.loanAmount as string | number | null) !== null,
        )
        if (withLoan.length === 0) {
            formMethods.setValue('loanAmount', null)
            return
        }
        const sorted = [...withLoan].sort((a, b) => (isBefore(new Date(a.recordDate), new Date(b.recordDate)) ? -1 : 1))
        const latest = sorted[sorted.length - 1]
        const v = parseOptionalLoanAmount(latest.loanAmount as string | number | null)
        if (v !== null) formMethods.setValue('loanAmount', v)
    }

    const setFormConvertibleActions = useCallback(
        (a: ActionFormWithId[]) => {
            if (JSON.stringify(a) === JSON.stringify(convertibleActions)) return

            const newConvertibleActions = a.map(setTempId) // make sure all investors have a tempId
            setConvertibleActions(newConvertibleActions)
            // issue.investors = investors
            const formConvertibleActions = newConvertibleActions.map((p) => {
                const { tempId, ...rest } = p
                return rest
            })
            formMethods.setValue('convertibleActions', formConvertibleActions)
        },
        [formMethods, convertibleActions],
    )
    useEffect(() => {
        setFormConvertibleActions(convertibleActions)
    }, [convertibleActions, setFormConvertibleActions])

    const nullEmptyStrings = (obj: Record<string, unknown>) => {
        Object.keys(obj).forEach((key) => {
            if (obj[key] === '') obj[key] = null
        })
    }
    const handleSubmitConvertibleAction = (newConvertibleAction: ActionFormWithId) => {
        nullEmptyStrings(newConvertibleAction)
        setIsCustomFormDirty(true)
        let next: ActionFormWithId[]
        if (newConvertibleAction.tempId) {
            next = convertibleActions.map((p) => (p.tempId === newConvertibleAction.tempId ? newConvertibleAction : p))
        } else {
            newConvertibleAction.tempId = uuidv4()
            next = convertibleActions.concat(newConvertibleAction)
        }
        syncLoanAmountFromLoanActions(next)
        setFormConvertibleActions(next)
    }

    const handleDelete = (convertibleAction: ActionFormWithId) => {
        const next = convertibleActions.filter((p) => p.tempId !== convertibleAction.tempId)
        syncLoanAmountFromLoanActions(next)
        setFormConvertibleActions(next)
    }

    if (!convertible) return null

    const sortedConvertibleActions = [...convertibleActions].sort((a, b) =>
        isBefore(new Date(a.recordDate), new Date(b.recordDate)) ? -1 : 1,
    )

    const totalAmountOutstanding = computeOutstandingLoanBalance(convertibleActions)

    return (
        <Grid spacing={2} direction='row' container>
            <Grid size={12}>
                <RegisteredCheckBox
                    label='Konvertibel återbetald eller konverterad'
                    name='completed'
                    onChange={() => setIsCustomFormDirty(true)}
                />
            </Grid>
            <Grid size={12} sx={{ height: '100%' }}>
                <Stack
                    direction='row'
                    sx={{
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant='subtitle1' sx={{ py: 2 }}>
                        Händelser
                    </Typography>
                    <Button onClick={() => newFormOpen()}>Lägg till händelse</Button>
                </Stack>
                {actionFormOpen ? (
                    <ActionDialog
                        convertible={convertible}
                        convertibleAction={actionToEdit}
                        totalAmountOutstanding={totalAmountOutstanding}
                        open={actionFormOpen}
                        handleClose={handleCloseEventDialog}
                        handleSubmitConvertibleAction={handleSubmitConvertibleAction}
                    />
                ) : null}
                <TableContainer>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Datum</TableCell>
                                <TableCell>Händelse</TableCell>
                                <TableCell>Återbetalning</TableCell>
                                <TableCell>Konverteringskurs</TableCell>
                                <TableCell>Antal aktier</TableCell>
                                <TableCell width='90px'>Belopp</TableCell>
                                <TableCell width='80px' />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedConvertibleActions.map((convertibleAction) => {
                                const { repayment, convertedShares, conversionPrice } = convertibleAction
                                let amount: number | null = null
                                let showOutflowNegative = true
                                if (convertibleAction.actionType === 'new_loan') {
                                    amount = parseOptionalLoanAmount(
                                        convertibleAction.loanAmount as string | number | null,
                                    )
                                    showOutflowNegative = false
                                } else if (repayment !== null) {
                                    amount = parseFloat(repayment)
                                } else if (convertedShares !== null && conversionPrice !== null) {
                                    amount = convertedShares * parseFloat(conversionPrice)
                                }
                                return (
                                    <TableRow key={convertibleAction.tempId || convertibleAction.id}>
                                        <TableCell>{formatDateYYYYMMDD(convertibleAction.recordDate, { locale })}</TableCell>
                                        <TableCell>
                                            {t(`entities.convertible.fields.${convertibleAction.actionType}`)}
                                        </TableCell>
                                        <TableCell>
                                            {convertibleAction.repayment !== null
                                                ? formatBigMoney(convertibleAction.repayment, { locale })
                                                : null}
                                        </TableCell>
                                        <TableCell>
                                            {formatMoney(convertibleAction.conversionPrice, {
                                                locale,
                                                minDecimals: 2,
                                                maxDecimals: 5,
                                            })}
                                        </TableCell>
                                        <TableCell>
                                            {formatInteger(convertibleAction.convertedShares, { locale })}
                                        </TableCell>
                                        <TableCell>
                                            {amount !== null
                                                ? showOutflowNegative
                                                    ? formatBigMoney(-amount, { locale })
                                                    : formatBigMoney(amount, { locale })
                                                : null}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction='row'>
                                                <IconButton
                                                    onClick={() => {
                                                        editFormOpen(convertibleAction)
                                                    }}
                                                    sx={{ px: '4px' }}
                                                >
                                                    <EditIcon color='warning' />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => {
                                                        handleDelete(convertibleAction)
                                                    }}
                                                    sx={{ px: '4px' }}
                                                >
                                                    <DeleteIcon color='error' />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                            <TableRow>
                                <TableCell height='51.75px' />
                                <TableCell>Summa</TableCell>
                                <TableCell colSpan={3} />
                                <TableCell>
                                    <Typography variant='body2'>
                                        {formatBigMoney(totalAmountOutstanding, { locale })}
                                    </Typography>
                                </TableCell>
                                <TableCell />
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>
    )
}

export default EventsFields
